import { Modal } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useState } from "react";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { ChangesSource, type DepartmentLocation, type RecurringShift } from "../../utils/models";
import { MedicalDepartmentAdditionsPopup } from "../MedicalDepartmentAdditionsPopup/MedicalDepartmentAdditionsPopup";
import { RecurringShift as RecurringShiftComponent, RecurringShiftComponentSource } from "./RecurringShift";
import { RecurringShiftMasterSchedule } from "./RecurringShiftMasterSchedule/RecurringShiftMasterSchedule";
import type { RecurringShiftMasterScheduleProps, RecurringShiftWeekPrefill } from "./RecurringShiftMasterSchedule/utils";
import type { RecurringShiftData, RecurringShiftDisplayMode } from "./util/useRecurringShiftState";

type Props = {
	renderWithoutModal: boolean;
	clickedNurseId?: number | undefined;
	existingRecurringShiftId?: number | undefined;
	onClose?: ((didUpdateOrCreate?: boolean) => void) | ((didUpdateOrCreate?: boolean) => Promise<void>);

	/**
	 * New Recurring Modal Props
	 */
	disableHidePopup?: boolean;
	clickedDate: DateTime;
	shouldAddNewShiftOnOpen: boolean;
	recurringShiftsData?: RecurringShift[];
	clickedLocation?: DepartmentLocation;

	/**
	 * Old Recurring Modal Props
	 */
	recurringShiftDisplayMode?: RecurringShiftDisplayMode | undefined;
	existingShiftStartTime?: DateTime | undefined;
	initialRecurringShiftData?: RecurringShiftData | undefined;
	source?: RecurringShiftComponentSource;
	isSynergyMS: boolean;

	/** Force the "new" v2 panel regardless of the `RecurringPanelV2Enabled` flag (other call sites stay flag-driven). */
	forceNewPanel?: boolean;
	/**
	 * Per-template-week prefill for the **v2** panel (`RecurringShiftWeekPrefill`) — a caller that
	 * opens this with a proposed schedule already on screen. Ignored by the old panel, which has its
	 * own single-recurrence `initialRecurringShiftData` shape and no way to vary days by week.
	 */
	prefilledWeeks?: RecurringShiftWeekPrefill[];
	/**
	 * Restrict the staff search to holders of this job — see `useRecurringShiftMasterScheduleState`.
	 *
	 * Reaches the V2 panel only. The legacy one below takes no filter: `recurring_panel_v2_enabled` is
	 * fully rolled out, so it renders for nobody but a demo user who toggles versions by hand.
	 */
	staffJobIdFilter?: string;
	/** Reaches the V2 panel only — see `RecurringShiftMasterScheduleProps`. */
	suggestStaffShiftDefaults?: RecurringShiftMasterScheduleProps["suggestStaffShiftDefaults"];
};

export const RecurringShiftPanelWrapper: FC<Props> = ({
	renderWithoutModal,
	clickedNurseId,
	existingRecurringShiftId,
	disableHidePopup,
	onClose,
	clickedDate,
	clickedLocation,
	recurringShiftsData,
	shouldAddNewShiftOnOpen,
	recurringShiftDisplayMode,
	existingShiftStartTime,
	initialRecurringShiftData,
	source,
	isSynergyMS,
	prefilledWeeks,
	staffJobIdFilter,
	suggestStaffShiftDefaults,
	// DEFAULT: omitted → flag-driven panel choice; callers opt into the new panel explicitly.
	forceNewPanel = false,
}) => {
	const { isAnyDemo } = useDepartmentFlags();
	const recurringPanelV2Enabled = useFeatureFlag(FEATURE_FLAGS.RecurringPanelV2Enabled);

	const [selectedMode, setSelectedMode] = useState<"new" | "old">(recurringPanelV2Enabled || forceNewPanel ? "new" : "old");

	if (isSynergyMS) {
		if (!clickedNurseId) {
			return (
				<MedicalDepartmentAdditionsPopup
					clickedDate={clickedDate}
					mode={source === RecurringShiftComponentSource.MasterSchedule ? "add-recurring" : "add-single"}
					facility={clickedLocation}
					onClose={onClose}
					hideCreateShiftsTab={true}
				/>
			);
		}
		return (
			<MedicalDepartmentAdditionsPopup
				clickedDate={clickedDate}
				mode={"edit-recurring"}
				facility={clickedLocation}
				onClose={onClose}
				hideCreateShiftsTab={true}
				clickedNurseId={clickedNurseId}
				existingRecurringShiftId={existingRecurringShiftId}
				recurringShiftsData={recurringShiftsData}
			/>
		);
	}
	if (selectedMode === "new") {
		return (
			<RecurringShiftMasterSchedule
				shouldAddNewShiftOnOpen={shouldAddNewShiftOnOpen}
				disableHidePopup={disableHidePopup}
				clickedDate={clickedDate}
				clickedNurseId={clickedNurseId}
				onClose={onClose}
				knownClickedRecurringShiftId={existingRecurringShiftId}
				recurringShiftsData={recurringShiftsData}
				renderWithoutModal={renderWithoutModal}
				clickedLocation={clickedLocation}
				onVersionChange={isAnyDemo ? setSelectedMode : undefined}
				prefilledWeeks={prefilledWeeks}
				staffJobIdFilter={staffJobIdFilter}
				suggestStaffShiftDefaults={suggestStaffShiftDefaults}
				source={source === "master_schedule" ? ChangesSource.MasterSchedule : ChangesSource.Profile}
			/>
		);
	}

	const body = (
		<RecurringShiftComponent
			source={source}
			onClose={onClose}
			recurringShiftDisplayMode={recurringShiftDisplayMode}
			existingShiftNurseId={clickedNurseId}
			existingShiftStartTime={existingShiftStartTime}
			existingRecurringShiftId={existingRecurringShiftId}
			clickedDate={clickedDate}
			initialRecurringShiftData={initialRecurringShiftData}
			onVersionChange={isAnyDemo ? setSelectedMode : undefined}
		/>
	);

	if (renderWithoutModal) {
		return body;
	}

	return (
		<Modal open={true}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					height: "100vh",
					width: "100vw",
				}}
			>
				<div
					style={{
						background: "white",
						height: "fit-content",
						width: "60%",
					}}
				>
					{body}
				</div>
			</div>
		</Modal>
	);
};
